import React, { useEffect, useState } from "react";
import { UtensilsCrossed, MapPin, Phone, Mail, Instagram, Facebook } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { fetchCategories } from "../services/categories.service";

/* TikTok icon بدون مكتبة */
const TikTokIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M16.6 3h-3.4v13.6a2.9 2.9 0 11-2.9-2.9c.2 0 .4 0 .6.1V9.8a6.3 6.3 0 00-.6 0A6.3 6.3 0 003.2 16 6.3 6.3 0 009.5 22.3a6.3 6.3 0 006.3-6.3V9.3c1.4 1 3 1.6 4.8 1.6V7.4A4.8 4.8 0 0116.6 3z" />
  </svg>
);

export default function Footer({ onNavigate }) {
  const { t, lang } = useI18n();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const langMap = { AR: "ar", EN: "en", TR: "tr" };

    fetchCategories({ lang: langMap[lang] || "ar" })
      .then((cats) => {
        if (!cancelled && Array.isArray(cats)) setCategories(cats);
      })
      .catch((err) => console.error("Footer fetch error:", err));

    return () => {
      cancelled = true;
    };
  }, [lang]);

  return (
    <footer className="bg-[#0a0a0a] text-gray-400 py-16 border-t border-[#c32228]/30 font-sans text-sm relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-[#c32228] opacity-5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">

          {/* Logo + description */}
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2 text-white">
              <div className="bg-[#c32228] p-2 rounded-full">
                <UtensilsCrossed size={20} />
              </div>

              <span className="text-2xl font-serif font-bold">
                {t("nav.brandName")}
                <span className="text-[#c32228]">
                  {t("nav.brandHighlight")}
                </span>
              </span>
            </div>

            <p className="leading-relaxed opacity-80">
              {t("footer.description")}
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-4 mt-2">
              <a
                href="https://www.instagram.com/sham_lokant"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center hover:bg-[#c32228] hover:text-white transition-all duration-300 border border-white/5"
              >
                <Instagram size={18} />
              </a>

              <a
                href="https://www.facebook.com/share/18PCSsMSEQ/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center hover:bg-[#c32228] hover:text-white transition-all duration-300 border border-white/5"
              >
                <Facebook size={18} />
              </a>

              <a
                href="https://www.tiktok.com/@sham.05383341950"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center hover:bg-[#c32228] hover:text-white transition-all duration-300 border border-white/5"
              >
                <TikTokIcon size={18} />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-white font-bold uppercase tracking-wider mb-6 relative inline-block">
              {t("footer.quickLinks")}
              <span className="absolute -bottom-2 right-0 w-8 h-0.5 bg-[#c32228]" />
            </h3>

            <ul className="flex flex-col gap-4">
              {[
                { name: t("nav.home"), id: "home" },
                { name: t("nav.categories"), id: "categories" },
                { name: t("nav.products"), id: "products" },
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => onNavigate?.(link.id)}
                    className="hover:text-[#c32228] transition-colors flex items-center gap-2"
                  >
                    <span className="w-1 h-1 bg-[#c32228] rounded-full" />
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-white font-bold uppercase tracking-wider mb-6 relative inline-block">
              {t("footer.menuTitle")}
              <span className="absolute -bottom-2 right-0 w-8 h-0.5 bg-[#c32228]" />
            </h3>

            <ul className="flex flex-col gap-4">
              {categories.slice(0, 4).map((cat, i) => (
                <li key={cat.id || i}>
                  <button
                    onClick={() => onNavigate?.("categories")}
                    className="hover:text-[#c32228] transition-colors flex items-center gap-2"
                  >
                    <span className="w-1 h-1 bg-[#c32228] rounded-full" />
                    {cat.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-bold uppercase tracking-wider mb-6 relative inline-block">
              {t("footer.contactUs")}
              <span className="absolute -bottom-2 right-0 w-8 h-0.5 bg-[#c32228]" />
            </h3>

            <ul className="flex flex-col gap-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} className="text-[#c32228] mt-1" />
                <a
                  href="https://maps.app.goo.gl/Guuzvavw3ZDCFWkM9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#c32228]"
                >
                  Gaziantep, Türkiye
                </a>
              </li>

              <li className="flex items-center gap-4">
                <Phone size={20} className="text-[#c32228]" />
                <a
                  href="tel:+905383341950"
                  className="hover:text-[#c32228]"
                >
                  +90 538 334 19 50
                </a>
              </li>

              <li className="flex items-center gap-4">
                <Mail size={20} className="text-[#c32228]" />
                <a
                  href="mailto:akik.vadisi.gida@gmail.com"
                  className="hover:text-[#c32228]"
                >
                  akik.vadisi.gida@gmail.com
                </a>
              </li>
            </ul>
          </div>

        </div>

        {/* Copyright & Developer Credit */}
        <div className="border-t border-white/10 pt-8 mt-4">
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-gray-500 text-sm">
              {t("footer.copyright")}
            </p>
            <p className="text-gray-600 text-xs flex items-center gap-1.5">
              {t("footer.madeBy")}{" "}
              <a
                href="https://www.instagram.com/muhammed._.270?igsh=czdvd2l6M2l4ZXU2"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#c32228] hover:text-white font-semibold transition-colors duration-300"
              >
                Muhammed Hasan
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
